import React from 'react';
import { Box, TextField, ToggleButtonGroup, ToggleButton, Autocomplete, FormControlLabel, Checkbox, Typography } from '@mui/material';
import CustomButton from 'shared/custom-button/CustomButton';
import { colors } from 'app/providers/theme/colors-type';
import styles from 'features/mite-policy/step-2/StepTwo.module.css';
import { IStepTwo } from 'entities/mite-policy/step-2/types/step-two-inerface';

interface PolicyholderStepProps {
    formState: IStepTwo;
    errors: Record<string, string>;
    handleChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (fieldName: keyof IStepTwo) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleDateChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    formatPhone: (phone: string) => string;
    handleCheckboxChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    handleToggleGenderChange: (fieldName: keyof IStepTwo) => (e: React.MouseEvent<HTMLElement>, newValue: string | null) => void;
    handleNextStep: () => void;
    emailInput: string;
    handleEmailInputChange: (event: React.ChangeEvent<{}>, newValue: string) => void;
    handleDomainSelect: (event: React.ChangeEvent<{}>, newValue: string | null) => void;
    filterDomains: (options: string[], state: { inputValue: string }) => string[];
    emailDomains: string[];
    requestProps: boolean;
    windowWidth: number;
    handlePhoneChange: (fieldName: keyof IStepTwo) => (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PolicyholderStep: React.FC<PolicyholderStepProps> = ({
    formState,
    errors,
    handleChange,
    handleBlur,
    handleDateChange,
    formatPhone,
    handleCheckboxChange,
    handleToggleGenderChange,
    handleNextStep,
    emailInput,
    handleEmailInputChange,
    handleDomainSelect,
    filterDomains,
    emailDomains,
    requestProps,
    windowWidth,
    handlePhoneChange
}) => {
    const handleParseFIO = () => {
        const fioArray = formState.Fio.split(' ');
        if (fioArray.length >= 1) {
            handleChange('LastName')({ target: { value: fioArray[0] } } as React.ChangeEvent<HTMLInputElement>);
        }
        if (fioArray.length >= 2) {
            handleChange('FirstName')({ target: { value: fioArray[1] } } as React.ChangeEvent<HTMLInputElement>);
        }
        if (fioArray.length >= 3) {
            handleChange('MiddleName')({ target: { value: fioArray[2] } } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    return (
        <Box>
            <Box className={styles.titleBox}>
                <h4>Страхователь</h4>
                <p>Тот, кто оформляет полис</p>
            </Box>
            <Box className={styles.userBox}>
                <TextField
                    name="Fio"
                    label="ФИО"
                    value={formState.Fio}
                    onChange={handleChange('Fio')}
                    onBlur={(e) => {
                        handleBlur('Fio')(e);
                        handleParseFIO();
                    }}
                    error={!!errors.Fio || !!errors.LastName || !!errors.FirstName || !!errors.MiddleName}
                    helperText={errors.Fio || errors.LastName || errors.FirstName || errors.MiddleName}
                    className={styles.Fio}
                />
            </Box>

            {requestProps && windowWidth <= 700 && (
                <Box className={styles.IDClientBox}>
                    <TextField
                        name="clientId"
                        label="ID клиента"
                        value={formState.clientId}
                        onChange={handleChange('clientId')}
                        onBlur={(e) => {
                            handleBlur('clientId')(e);
                        }}
                        error={!!errors.clientId}
                        helperText={errors.clientId}
                        className={styles.clientId}
                        type='number'
                    />
                </Box>
            )}

            <Box className={styles.secondLineBox}>
                {requestProps && windowWidth > 700 && (
                    <TextField
                        name="clientId"
                        label="ID клиента"
                        value={formState.clientId}
                        onChange={handleChange('clientId')}
                        onBlur={(e) => {
                            handleBlur('clientId')(e);
                        }}
                        error={!!errors.clientId}
                        helperText={errors.clientId}
                        type='number'
                    />
                )}
                <TextField
                    name="BirthDate"
                    label="Дата рождения"
                    variant="outlined"
                    value={formState.BirthDate}
                    onChange={handleDateChange('BirthDate')}
                    placeholder="дд.мм.гггг"
                    onBlur={handleBlur('BirthDate')}
                    error={!!errors.BirthDate}
                    helperText={errors.BirthDate}
                />
                <Box className={styles.errorBoxGender}>
                    <ToggleButtonGroup
                        value={formState.Gender}
                        exclusive
                        onChange={handleToggleGenderChange('Gender')}
                        aria-label="Gender"
                        sx={{
                            width: '100%',
                            display: 'flex',
                            '& .MuiToggleButtonGroup-grouped': {
                                flex: 1,
                                minWidth: 0,
                            },
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                borderRadius: '4px !important',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                height: '40px !important'
                            },
                            '& .Mui-selected': {
                                backgroundColor: `${colors.primary} !important`,
                                color: 'white !important',
                                '&:hover': {
                                    backgroundColor: `${colors.primary} !important`,
                                },
                            },
                        }}
                    >
                        <ToggleButton value="Ж" aria-label="Женщина">
                            Женщина
                        </ToggleButton>
                        <ToggleButton value="М" aria-label="Мужчина">
                            Мужчина
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Box className={styles.errorInsuanceGenderBox}>
                        {errors[`Gender`] && (
                            <Typography color="error" variant="caption">
                                {errors[`Gender`]}
                            </Typography>
                        )}
                    </Box>
                </Box>

            </Box>

            <Box className={styles.secondLineBox}>
                <TextField
                    name="Phone"
                    label="Телефон"
                    variant="outlined"
                    className={styles.fullWidth}
                    value={formatPhone(formState.Phone)}
                    onChange={handlePhoneChange('Phone')}
                    onBlur={handleBlur('Phone')}
                    error={!!errors.Phone}
                    helperText={errors.Phone}
                />
                <Autocomplete
                    freeSolo
                    options={emailDomains}
                    value={null}
                    inputValue={emailInput}
                    onInputChange={handleEmailInputChange}
                    onChange={handleDomainSelect}
                    filterOptions={filterDomains}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name="Email"
                            label="Почта"
                            variant="outlined"
                            className={styles.fullWidth}
                            error={!!errors.Email}
                            helperText={errors.Email}
                        />
                    )}
                />
            </Box>
            <Box className={styles.btnBox}>
                <CustomButton
                    title="Далее"
                    handleClick={handleNextStep}
                    textColor="white"
                    backgroundColor={colors.primary}
                    width="150px"
                    gap="10px"
                    borderRadius='4px'
                    padding='3px'
                />
            </Box>
        </Box>
    );
};
